import { useState, useId } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ConstructionOrderLine } from '@saiens/shared/types';

interface LineItemSectionProps {
  title: string;
  icon: React.ReactNode;
  items: ConstructionOrderLine[];
  defaultExpanded?: boolean;
}

export function LineItemSection({
  title,
  icon,
  items,
  defaultExpanded = false,
}: LineItemSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentId = useId();

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl bg-white shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between p-4"
        aria-expanded={expanded}
        aria-controls={contentId}
      >
        <div className="flex items-center gap-2">
          <span className="h-5 w-5 text-gray-500">{icon}</span>
          <span className="font-semibold text-gray-900">{title}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {items.length}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div id={contentId} className="border-t border-gray-100 px-4 pb-4">
          <div className="divide-y divide-gray-50">
            {items.map((item) => (
              <div key={item.id} className="py-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">
                      {item.product_name || item.name || '未命名項目'}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                      {item.length > 0 && item.depth > 0 && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5">
                          {item.length} x {item.depth}
                        </span>
                      )}
                      {item.saiens_color && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5">
                          {item.saiens_color}
                        </span>
                      )}
                      {item.standard && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5">
                          {item.standard}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    x{item.product_uom_qty}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
